import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LogOut,
  X,
  ChevronRight,
  PanelLeft,
  Globe,
  HelpCircle,
} from 'lucide-react';
import { YimaChatbot } from '../../components/YimaChatbot';
import logoImage from '../../assets/images/logo.webp';
import type { SidebarGroup } from './sidebarConfig';

function PendingBadge({ count, collapsed }: { count: number; collapsed?: boolean }) {
  if (count <= 0) return null;

  return (
    <AnimatePresence>
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full shadow-sm ${collapsed ? 'absolute -top-1 -right-1' : 'ml-auto'}`}
      >
        {count > 99 ? '99+' : count}
      </motion.span>
    </AnimatePresence>
  );
}

interface AdminSidebarProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  filteredGroups: SidebarGroup[];
  isActive: (path: string) => boolean;
  user: { firstName: string; lastName: string; email: string } | null;
  badgeCounts: { pendingMembers: number; unreadContacts: number };
  onLogout: () => void;
  startTour: (tour: 'dashboard') => void;
  tourActive: boolean;
}

export function AdminSidebar({
  sidebarCollapsed,
  setSidebarCollapsed,
  mobileMenuOpen,
  setMobileMenuOpen,
  filteredGroups,
  isActive,
  user,
  badgeCounts,
  onLogout,
  startTour,
  tourActive,
}: AdminSidebarProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            <MobileSidebar
              groups={filteredGroups}
              isActive={isActive}
              onClose={() => setMobileMenuOpen(false)}
              user={user}
              onLogout={onLogout}
              t={t}
              logo={logoImage}
              badgeCounts={badgeCounts}
            />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside
        data-tour="sidebar"
        className={`hidden lg:flex flex-col fixed left-0 top-16 bottom-0 z-40 bg-white border-r border-slate-200 transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'w-[72px]' : 'w-64'}`}
      >
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="mx-auto mt-3 p-1.5 rounded-lg text-slate-600 hover:text-slate-600 hover:bg-slate-100 transition-all duration-200"
            title={t('admin.layout.expandSidebar')}
          >
            <PanelLeft className="w-4 h-4" />
          </button>
        )}

        <nav className={`flex-1 overflow-y-auto ${sidebarCollapsed ? 'px-2 py-4' : 'px-4 py-6'} space-y-6`}>
          {filteredGroups.map((group, groupIndex) => (
            <div key={group.category}>
              {!sidebarCollapsed && (
                <div className="px-3 mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    {group.category}
                  </span>
                </div>
              )}

              {sidebarCollapsed && groupIndex > 0 && (
                <div className="border-t border-slate-100 my-3" />
              )}

              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  const badgeCount = item.badgeKey ? badgeCounts[item.badgeKey] : 0;

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 ${active
                        ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/5 backdrop-blur-sm text-[#1E40AF] shadow-sm border border-blue-200/30'
                        : 'text-slate-600 hover:bg-slate-100/80 hover:backdrop-blur-sm hover:text-slate-900'
                        } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                      title={sidebarCollapsed ? item.label : undefined}
                      {...(item.href === '/admin/settings' ? { 'data-tour': 'sidebar-settings' } : {})}
                    >
                      {active && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-gradient-to-b from-[#60A5FA] via-[#3B82F6] to-[#60A5FA] rounded-r-full shadow-[0_0_12px_rgba(96,165,250,0.6)] backdrop-blur-sm" />
                      )}

                      <div className="relative">
                        <Icon className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${active ? 'text-[#1E40AF] scale-110 drop-shadow-sm' : ''}`} />
                        {sidebarCollapsed && <PendingBadge count={badgeCount} collapsed />}
                      </div>

                      {!sidebarCollapsed && (
                        <>
                          <span className={`text-sm transition-all duration-300 ${active ? 'font-semibold tracking-wide' : 'font-normal'}`}>
                            {item.label}
                          </span>
                          <PendingBadge count={badgeCount} />
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div data-tour="sidebar-user" className={`border-t border-slate-100 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
          {!sidebarCollapsed && user && (
            <div className="px-3 py-3 mb-2 bg-slate-50 rounded-xl">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-slate-600 truncate">{user.email}</p>
            </div>
          )}
          <button
            onClick={() => startTour('dashboard')}
            disabled={tourActive}
            className={`group flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200 disabled:opacity-50 ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
            title={sidebarCollapsed ? t('onboarding.restartTour', 'Guided tour') : undefined}
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0 group-hover:text-blue-500" />
            {!sidebarCollapsed && <span className="text-sm">{t('onboarding.restartTour', 'Guided tour')}</span>}
          </button>
          <button
            onClick={onLogout}
            className={`group flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200 ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
            title={sidebarCollapsed ? t('nav.logout') : undefined}
          >
            <LogOut className="w-5 h-5 flex-shrink-0 group-hover:text-red-500" />
            {!sidebarCollapsed && <span className="text-sm">{t('nav.logout')}</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

function MobileSidebar({
  groups,
  isActive,
  onClose,
  user,
  onLogout,
  t,
  logo,
  badgeCounts,
}: {
  groups: SidebarGroup[];
  isActive: (path: string) => boolean;
  onClose: () => void;
  user: { firstName: string; lastName: string; email: string } | null;
  onLogout: () => void;
  t: (key: string) => string;
  logo: string;
  badgeCounts: { pendingMembers: number; unreadContacts: number };
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
        <Link to="/admin" className="flex items-center gap-3" onClick={onClose}>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1E40AF] to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <img
              src={logo}
              alt="Logo YIM"
              width={24}
              height={24}
              loading="eager"
              decoding="async"
              className="w-6 h-6 object-contain"
            />
          </div>
          <span className="font-heading font-bold text-slate-900 text-lg tracking-tight">
            YIM
          </span>
        </Link>
        <button
          onClick={onClose}
          className="p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-all duration-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        {groups.map((group) => (
          <div key={group.category}>
            <div className="px-3 mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                {group.category}
              </span>
            </div>

            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                const badgeCount = item.badgeKey ? badgeCounts[item.badgeKey] : 0;

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${active
                      ? 'bg-blue-50 text-[#1E40AF]'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                  >
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#1E40AF] rounded-r-full" />
                    )}

                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-[#1E40AF]' : ''}`} />
                    <span className={`text-sm ${active ? 'font-medium' : 'font-normal'}`}>
                      {item.label}
                    </span>
                    <PendingBadge count={badgeCount} />
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-100">
        {user && (
          <div className="px-3 py-3 mb-2 bg-slate-50 rounded-xl">
            <p className="text-sm font-medium text-slate-900">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-slate-600">{user.email}</p>
          </div>
        )}

        <Link
          to="/"
          onClick={onClose}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all duration-200 mb-2"
        >
          <Globe className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{t('admin.topbar.viewWebsite')}</span>
          <ChevronRight className="w-4 h-4 ml-auto" />
        </Link>

        <button
          onClick={onLogout}
          className="group flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200"
        >
          <LogOut className="w-5 h-5 flex-shrink-0 group-hover:text-red-500" />
          <span className="text-sm">{t('nav.logout')}</span>
        </button>
      </div>

      <YimaChatbot />
    </div>
  );
}
